import { Action } from '@reduxjs/toolkit';
import React, { PropsWithChildren, Ref, useCallback, useEffect } from 'react';
import mergeRefs from 'react-merge-refs';
import { useSelector } from 'react-redux';
import { Dispatch } from 'redux';

import { get } from '@eika-infrastruktur/request';
import '@eika/attention-message-style';
import { Input } from '@eika/input';
import { Loader } from '@eika/loader';
import { Select } from '@eika/select';

import { Error } from '../../components/error/error';
import { SporsmalHeader } from '../../components/sporsmal-header/sporsmal-header';
import { useBankregnr } from '../../config';
import { getRequestOptions } from '../../meldskade/reducer/skademelding-actions';
import { AppState } from '../../reducers';
import { failure, loading, RemoteData, RemoteDataType, success } from '../../reducers/remoteData';
import { Skademeldingstype, VerkstedWebDTO } from '../../types/generated-types';
import { hentVerkstederUrl } from '../../urler';
import { SideDetail, SideType } from '../../utils/piwik-utils';
import { formatTelefonnummer } from '../format';
import { findChosenForsikringFromSporsmal } from '../forsikring/forsikring-types';
import { FocusableHtmlElement, useFocusRef } from '../hooks';
import { flattenBesvarteSporsmal } from '../reducer/current-steg';
import { updateBilmerke, updateFetchVerkstederStatus, updatePostnummer } from '../reducer/sporsmalsflyt-actions';
import { Bilmerke } from '../reducer/sporsmalsflyt-types';
import { useSporsmalsflytDispatch } from '../reducer/sporsmalsflytDispatchProvider';
import { CurrentVerkstedsporsmal, isValidPostnummer, Verksted } from './verksted-types';

import './verksted.scss';

const VerkstedListeWrapper: React.FC<PropsWithChildren> = ({ children }) => (
    <div className="verkstedliste">
        <h2 className="verkstedliste-header">Dine verkstedsalternativer</h2>
        {children}
    </div>
);

export const VerkstedVisning = ({ verksted }: { verksted: Verksted }) => (
    <li>
        <address className="garage-address">
            <strong>{verksted.navn}</strong> <br />
            {verksted.adresse} <br />
            {verksted.postnummer.value} {verksted.poststed} <br />
            <a className="verksted-telefonnummer" href={`tel:${verksted.telefonnummer}`}>
                {formatTelefonnummer(verksted.telefonnummer)}
            </a>
        </address>
    </li>
);

const VerkstedListeSuccess = ({ verksteder }: { verksteder: Verksted[] }) => {
    if (verksteder.length === 0) {
        return (
            <VerkstedListeWrapper>
                <div className="sgw-attention-message verksted-tom-liste-message type-info">
                    <span className="sgw-icon">
                        <span className="icon icon--24 icon--info-24" />
                    </span>
                    <div>
                        <p className="message-text">Vi fant ingen verksteder i nærheten av deg som vi anbefaler.</p>
                        <p className="message-text infoboks-andre-avsnitt">
                            Vi ber deg ta kontakt med et autorisert verksted for taksering og reparasjon, verkstedet må
                            benytte takseringssystemet DBS. Oppgi ditt skadenummer til verkstedet ved taksering.
                        </p>
                    </div>
                </div>
            </VerkstedListeWrapper>
        );
    }
    return (
        <VerkstedListeWrapper>
            <p>
                Ta kontakt med ett av disse verkstedene for å avtale tid for vurdering av skadene på kjøretøyet. Dette
                gjør du etter at du har sendt inn skademeldingen.
            </p>
            <ol>
                {verksteder.map((verksted) => (
                    <VerkstedVisning verksted={verksted} key={verksted.navn + verksted.id} />
                ))}
            </ol>
        </VerkstedListeWrapper>
    );
};

interface VerkstedListeProps {
    verksteder: RemoteData<Verksted[]>;
    sideDetail: SideDetail;
    onRetryFetching: () => void;
}

const VerkstedListe = ({ verksteder, sideDetail, onRetryFetching }: VerkstedListeProps) => {
    switch (verksteder.type) {
        case RemoteDataType.NOT_ASKED:
            return null;
        case RemoteDataType.LOADING:
            return (
                <VerkstedListeWrapper>
                    <div className="verksted-spinner">
                        <Loader ariaLabel="Henter verksteder" />
                    </div>
                </VerkstedListeWrapper>
            );
        case RemoteDataType.FAILURE:
            return (
                <VerkstedListeWrapper>
                    <Error
                        headerText="Her gikk noe galt"
                        bodyText="Vi klarte ikke å hente forslag til verksteder. Gå videre eller prøv igjen."
                        errorButtonProps={{
                            buttonText: 'Prøv igjen',
                            buttonDataTrackId: 'error-button-prov-igjen-verksted',
                            onButtonClick: onRetryFetching,
                            sideDetail,
                        }}
                    />
                </VerkstedListeWrapper>
            );
        default:
            return <VerkstedListeSuccess verksteder={verksteder.data} />;
    }
};

const postnummerFeilmelding = (showFeilmelding: boolean, postnummer: string): string | undefined => {
    if (showFeilmelding && !isValidPostnummer(postnummer)) {
        return 'Skriv inn et gyldig postnummer, fire siffer';
    }
    return undefined;
};

const bilmerkeFeilmelding = (showFeilmelding: boolean, bilmerke?: Bilmerke): string | undefined => {
    if (showFeilmelding && !bilmerke) {
        return 'Velg et bilmerke. Hvis du ikke finner riktig bilmerke kan du velge "Ukjent bilmerke"';
    }
    return undefined;
};

const getVerksteder =
    (
        postnummer: string,
        bilmerke: Bilmerke,
        bankregnr: string,
        skademeldingstype: Skademeldingstype,
        forsikringsnummer: string,
    ) =>
    async (dispatch: Dispatch<Action>) => {
        dispatch(updateFetchVerkstederStatus(loading(), postnummer, bilmerke));
        try {
            const verksteder: VerkstedWebDTO[] = await get(
                hentVerkstederUrl(postnummer, bilmerke, skademeldingstype, forsikringsnummer),
                getRequestOptions(bankregnr),
            );

            dispatch(updateFetchVerkstederStatus(success(verksteder), postnummer, bilmerke));
        } catch (e) {
            dispatch(updateFetchVerkstederStatus(failure(e), postnummer, bilmerke));
        }
    };

interface Props {
    currentSporsmal: CurrentVerkstedsporsmal;
    showFeilmeldinger: boolean;
    focusOnMount: boolean;
    errorRef?: Ref<FocusableHtmlElement>;
}

export const VerkstedSporsmal = ({ currentSporsmal, showFeilmeldinger, errorRef, focusOnMount = true }: Props) => {
    const ref = useFocusRef<HTMLInputElement>(focusOnMount);
    const dispatch = useSporsmalsflytDispatch();
    const bankregnr = useBankregnr();
    const state = useSelector((state: AppState) => state);
    const {
        skadetype,
        sporsmalsflytState: { besvarteSporsmal },
    } = state.skademelding;
    const forsikring = findChosenForsikringFromSporsmal(flattenBesvarteSporsmal(besvarteSporsmal));

    const forsikringsnummer = forsikring !== null ? forsikring.forsikringsnummer : '';

    const fetchVerksteder = useCallback(() => {
        if (isValidPostnummer(currentSporsmal.postnummer) && currentSporsmal.bilmerke) {
            dispatch(
                getVerksteder(
                    currentSporsmal.postnummer,
                    currentSporsmal.bilmerke,
                    bankregnr,
                    skadetype,
                    forsikringsnummer,
                ),
            );
        }
    }, [dispatch, currentSporsmal.postnummer, currentSporsmal.bilmerke, bankregnr, skadetype, forsikringsnummer]);

    useEffect(() => {
        fetchVerksteder();
    }, [fetchVerksteder]);

    const onPostnummerChange = (value: { target: { value: string } }) => {
        dispatch(updatePostnummer(value.target.value));
    };

    const onBilmerkeChange = (event: { target: { value: string } }) => {
        const bilmerke = currentSporsmal.sporsmal.bilmerker.find((bilmerke) => bilmerke.value === event.target.value);
        dispatch(updateBilmerke(bilmerke));
    };

    return (
        <div className="step-wrapper container__standard-width verksted-wrapper">
            <SporsmalHeader
                tittel={currentSporsmal.sporsmal.tittel}
                ingress="Oppgi postnummer og bilmerke for å få oversikt over avtaleverksteder i nærheten av deg."
            >
                <div className="verksted-postnummer-wrapper">
                    <Input
                        label="Postnummer"
                        value={currentSporsmal.postnummer}
                        onChange={onPostnummerChange}
                        className="verksted-postnummer-input"
                        id={`verksted-sporsmal-${currentSporsmal.sporsmal.id}`}
                        autoComplete="postal-code"
                        inputMode="numeric"
                        error={postnummerFeilmelding(showFeilmeldinger, currentSporsmal.postnummer)}
                        ref={mergeRefs([ref, !isValidPostnummer(currentSporsmal.postnummer) ? errorRef : undefined])}
                    />
                </div>
                <Select
                    label="Velg bilmerke"
                    values={[{ text: 'Velg bilmerke', value: 'Velg bilmerke ' }].concat(
                        currentSporsmal.sporsmal.bilmerker.map((bilmerke: Bilmerke) => ({
                            text: bilmerke.name,
                            value: bilmerke.value,
                        })),
                    )}
                    value={currentSporsmal.bilmerke?.value}
                    onChange={onBilmerkeChange}
                    className="verksted-bilmerke-dropdown"
                    error={bilmerkeFeilmelding(showFeilmeldinger, currentSporsmal.bilmerke)}
                    ref={
                        isValidPostnummer(currentSporsmal.postnummer) && !currentSporsmal.bilmerke
                            ? (errorRef as Ref<HTMLSelectElement>)
                            : undefined
                    }
                />
                <div aria-live="polite">
                    <VerkstedListe
                        verksteder={currentSporsmal.verksteder}
                        onRetryFetching={fetchVerksteder}
                        sideDetail={{
                            type: SideType.Skjemaside,
                            skademeldingstype: skadetype,
                            currentSporsmalId: currentSporsmal.id,
                        }}
                    />
                </div>
            </SporsmalHeader>
        </div>
    );
};
